import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BRAND_PALETTES, PALETTE_CSS_VAR_MAP } from './brandPalettes';

const STORAGE_KEY      = 'ti-dev-theme-v2';
const MODE_STORAGE_KEY = 'ti-color-mode';
const CORNERS_STORAGE_KEY = 'ti-corner-style';

const DEFAULT_THEME_KEY = 'lagoon';
const DEFAULT_MODE      = 'dark';
const DEFAULT_CORNERS   = 'sharp';

const defaultContextValue = {
  palette:    BRAND_PALETTES[DEFAULT_THEME_KEY],
  themeKey:   DEFAULT_THEME_KEY,
  setTheme:   (_key)  => {},
  mode:       DEFAULT_MODE,
  setMode:    (_mode) => {},
  toggleMode: ()      => {},
  corners:      DEFAULT_CORNERS,
  setCorners:   (_corners) => {},
  toggleCorners: ()        => {},
};

const rgb = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
const tuple = ([r, g, b]) => `${r}, ${g}, ${b}`;

/* ─── Mode-dependent surface + foreground tokens ──────────────
   These are the semantic colors all components consume. They flip
   wholesale when the user toggles light/dark, while brand accent
   palette stays the same. */

const DARK_TOKENS = {
  /* Surface tuples used in rgba() for transparent layers */
  '--ti-rgb-surface-page-0': '8, 9, 13',
  '--ti-rgb-surface-page-1': '11, 13, 20',
  '--ti-rgb-surface-card-0': '15, 19, 40',
  '--ti-rgb-surface-card-1': '9, 12, 28',
  '--ti-rgb-surface-glass':  '255, 255, 255',
  /* Foreground tuples (off-white text) */
  '--ti-rgb-fg':              '244, 247, 251',
  '--ti-rgb-fg-strong':       '244, 247, 251',
  '--ti-rgb-fg-soft':         '255, 255, 255',
  '--ti-rgb-fg-on-accent':    '255, 255, 255',
  /* Border source — used as rgba(var(--ti-rgb-border), α) */
  '--ti-rgb-border':          '255, 255, 255',
  /* Static color resolutions for convenience */
  '--ti-color-fg':            'rgba(244, 247, 251, 0.95)',
  '--ti-color-fg-muted':      'rgba(255, 255, 255, 0.55)',
  '--ti-color-fg-subtle':     'rgba(255, 255, 255, 0.4)',
  '--ti-color-bg-page':       'rgb(8, 9, 13)',
  '--ti-color-bg-card':       'rgba(15, 19, 40, 0.72)',
  '--ti-color-bg-elevated':   'rgba(20, 25, 45, 0.94)',
  '--ti-color-border':        'rgba(255, 255, 255, 0.08)',
  '--ti-color-border-strong': 'rgba(255, 255, 255, 0.16)',
  /* Mode-aware tunables */
  '--ti-shadow-strength':     '0.55',
  '--ti-grain-opacity':       '0.030',
  '--ti-aurora-opacity':      '0.62',
  '--ti-glass-blend':         'normal',
  '--ti-card-noise-blend':    'soft-light',
  '--ti-color-scheme':        'dark',
};

const LIGHT_TOKENS = {
  /* Surface tuples — light blue-gray base, near-white cards */
  '--ti-rgb-surface-page-0': '244, 246, 251',
  '--ti-rgb-surface-page-1': '255, 255, 255',
  '--ti-rgb-surface-card-0': '255, 255, 255',
  '--ti-rgb-surface-card-1': '248, 250, 254',
  '--ti-rgb-surface-glass':  '10, 14, 24',
  /* Foreground tuples (deep slate text on light bg) */
  '--ti-rgb-fg':              '15, 22, 36',
  '--ti-rgb-fg-strong':       '8, 12, 22',
  '--ti-rgb-fg-soft':         '15, 22, 36',
  '--ti-rgb-fg-on-accent':    '255, 255, 255',
  /* Border source — dark hairline */
  '--ti-rgb-border':          '15, 22, 36',
  /* Static resolutions */
  '--ti-color-fg':            'rgba(15, 22, 36, 0.96)',
  '--ti-color-fg-muted':      'rgba(15, 22, 36, 0.62)',
  '--ti-color-fg-subtle':     'rgba(15, 22, 36, 0.46)',
  '--ti-color-bg-page':       'rgb(244, 246, 251)',
  '--ti-color-bg-card':       'rgba(255, 255, 255, 0.92)',
  '--ti-color-bg-elevated':   'rgba(255, 255, 255, 1)',
  '--ti-color-border':        'rgba(15, 22, 36, 0.10)',
  '--ti-color-border-strong': 'rgba(15, 22, 36, 0.18)',
  /* Tunables */
  '--ti-shadow-strength':     '0.14',
  '--ti-grain-opacity':       '0.018',
  '--ti-aurora-opacity':      '0.22',
  '--ti-glass-blend':         'normal',
  '--ti-card-noise-blend':    'multiply',
  '--ti-color-scheme':        'light',
};

/* When the user is in light mode, the brand palette's `surfaceDeep0/1` would
   produce a near-black page bg — that's wrong on light. So we ALSO override
   those legacy vars in light mode to keep older CSS that consumes them
   automatically picking up the new mode. */
const LIGHT_LEGACY_OVERRIDES = {
  '--ti-surface-deep-0': 'rgb(244, 246, 251)',
  '--ti-surface-deep-1': 'rgb(255, 255, 255)',
};

export const BrandThemeContext = createContext(defaultContextValue);

/**
 * The site ships dark-only — no light-mode entry point (the manual toggle was
 * removed from the header). Always start dark; ignore any stale `light` value
 * a return visitor's browser may still have from before the toggle existed.
 * `setMode`/`toggleMode` stay live in the context API because the internal CMS
 * builder (DynamicPage.jsx) uses `setMode(page.mode)` for per-page previews —
 * that's an explicit authoring choice, not the auto/implicit light mode this
 * guards against.
 */
function getInitialMode() {
  return DEFAULT_MODE;
}

/**
 * Provides the active BrandPalette + color mode to the React tree and injects
 * CSS custom properties onto :root whenever palette or mode changes.
 */
export function BrandThemeProvider({ children }) {
  const [themeKey, setThemeKeyState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored && BRAND_PALETTES[stored] ? stored : DEFAULT_THEME_KEY;
    } catch { return DEFAULT_THEME_KEY; }
  });

  const [mode, setModeState] = useState(getInitialMode);

  const [corners, setCornersState] = useState(() => {
    try {
      const stored = localStorage.getItem(CORNERS_STORAGE_KEY);
      return stored === 'sharp' || stored === 'rounded' ? stored : DEFAULT_CORNERS;
    } catch { return DEFAULT_CORNERS; }
  });

  const palette = useMemo(
    () => BRAND_PALETTES[themeKey] ?? BRAND_PALETTES[DEFAULT_THEME_KEY],
    [themeKey]
  );

  /* Inject CSS variables whenever palette or mode changes */
  useEffect(() => {
    const root = document.documentElement;

    /* ─── 1. Brand accent palette (mode-independent) ─── */
    Object.entries(PALETTE_CSS_VAR_MAP).forEach(([key, cssVar]) => {
      root.style.setProperty(cssVar, palette[key].join(', '));
    });

    /* Candle up/down tuples — exposed so themed SVG product posters can colour
       their candles from the active palette. (Canvas-only in the palette map.) */
    root.style.setProperty('--ti-rgb-data-up', palette.dataUp.join(', '));
    root.style.setProperty('--ti-rgb-data-down', palette.dataDown.join(', '));

    /* ─── 2. Legacy surface tokens — dark mode uses palette values,
            light mode overrides with bright surfaces. ─── */
    if (mode === 'dark') {
      root.style.setProperty('--ti-surface-deep-0', `rgb(${palette.surfaceDeep0.join(',')})`);
      root.style.setProperty('--ti-surface-deep-1', `rgb(${palette.surfaceDeep1.join(',')})`);
    } else {
      Object.entries(LIGHT_LEGACY_OVERRIDES).forEach(([k, v]) => root.style.setProperty(k, v));
    }

    /* ─── 3. Mode-dependent semantic tokens ─── */
    const tokens = mode === 'light' ? LIGHT_TOKENS : DARK_TOKENS;
    Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));

    /* ─── 4. Bridge legacy SCSS-era aliases ─── */
    root.style.setProperty('--color-primary',       rgb(palette.cyan));
    root.style.setProperty('--color-primary-alt',   rgb(palette.electric));
    root.style.setProperty('--color-primary-light', rgb(palette.ice));
    root.style.setProperty('--color-primary-rgba',  rgb(palette.cyan));
    root.style.setProperty('--color-secondary',     rgb(palette.teal));
    root.style.setProperty('--color-accent',        rgb(palette.magenta));
    root.style.setProperty('--color-brand-pink',    rgb(palette.magenta));
    root.style.setProperty('--color-purple',        rgb(palette.purpleMid));

    /* ─── 5. Set the mode attribute so CSS can branch fine-grained
            via [data-ti-mode="light"] selectors. ─── */
    root.setAttribute('data-ti-mode', mode);

    /* Persist */
    try { localStorage.setItem(STORAGE_KEY, themeKey); } catch {}
    try { localStorage.setItem(MODE_STORAGE_KEY, mode); } catch {}
  }, [themeKey, palette, mode]);

  /* Corner style — flips every rounded border to square site-wide via a single
     root attribute. CSS does the heavy lifting (see assets/css/ti-corner-style.css). */
  useEffect(() => {
    document.documentElement.setAttribute('data-ti-corners', corners);
    try { localStorage.setItem(CORNERS_STORAGE_KEY, corners); } catch {}
  }, [corners]);

  const setTheme = useCallback((key) => {
    if (BRAND_PALETTES[key]) setThemeKeyState(key);
  }, []);

  const setMode = useCallback((next) => {
    if (next === 'light' || next === 'dark') setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const setCorners = useCallback((next) => {
    if (next === 'rounded' || next === 'sharp') setCornersState(next);
  }, []);

  const toggleCorners = useCallback(() => {
    setCornersState(prev => prev === 'sharp' ? 'rounded' : 'sharp');
  }, []);

  const contextValue = useMemo(
    () => ({ palette, themeKey, setTheme, mode, setMode, toggleMode, corners, setCorners, toggleCorners }),
    [palette, themeKey, setTheme, mode, setMode, toggleMode, corners, setCorners, toggleCorners]
  );

  return (
    <BrandThemeContext.Provider value={contextValue}>
      {children}
    </BrandThemeContext.Provider>
  );
}

/** @returns {{
 *   palette: import('./brandPalettes').BrandPalette,
 *   themeKey: string,
 *   setTheme: (key: string) => void,
 *   mode: 'light' | 'dark',
 *   setMode: (mode: 'light' | 'dark') => void,
 *   toggleMode: () => void,
 *   corners: 'rounded' | 'sharp',
 *   setCorners: (corners: 'rounded' | 'sharp') => void,
 *   toggleCorners: () => void,
 * }} */
export const useBrandTheme = () => useContext(BrandThemeContext);

/* eslint-disable no-unused-vars */
// `tuple` reserved for future per-token use; lint warns otherwise.
void tuple;
